// §4.8 SHIP QUEUE (floor PWA) — spec 09 §4.8, P3-D PR-D3, D-286.
//
// "Big-button list of orders ready at this facility, sorted by promise date;
// badge per order: lines count, all-reserved indicator." The list is
// `GET /v1/orders?shippable=true` — SHIPPABLE = {confirmed, in_production,
// ready} (D-286 ruling 2; `ready` is written only by §3.6's audited override,
// D-288, so most rows here are `confirmed` in practice) — narrowed for a
// storekeeper to their own
// facilities by the widened ship-relevant read arm, so the server decides
// "at this facility", never the client.
//
// THE SORT IS BY ORDER NUMBER IN PRACTICE, stated rather than hidden:
// `promise_date` is NULL on every P3 row (module 03's CTP is absent, §3.5d),
// so the promise-first order the spec asks for degrades to the order number,
// which is what the paper pick list on the bench is sorted by anyway. The
// comparator is written for the day the dates arrive and unit-tested on both
// halves.
//
// ONE TAP is the whole screen: the row IS the link into the pick screen
// (`/floor/ship/{orderId}`), because §3.7's "≤3 clicks" budget starts here and
// a card with a separate "Open" button inside it is a card that costs two.
//
// OFFLINE: a cached snapshot with its age (the receive list's posture). The
// pick screen behind a row is where a stale queue is discovered — the open
// itself is online-only (floor-ship.tsx's header), so a row tapped in a dead
// zone says so there rather than queuing a document nobody asked for.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import {
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { floorForbidden, floorRequestId } from "../lib/floor-errors";
import { readCacheKey } from "../lib/floor-read-cache";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import { rolesAreKnown } from "../../../lib/role-codes";
import { SALES_ROLE_GRANTS, useSalesPermissions } from "../../office/sales/hooks/permissions";
import "./floor-ship.css";

type S = components["schemas"];
type OrderListItem = S["OrderListItem"];
type OrderListResponse = S["OrderListResponse"];

/** The one cache slot the queue lives in; the pick screen reads it back for
 * the order NUMBER before its own detail has loaded. */
export const SHIP_QUEUE_CACHE_KEY = readCacheKey("ship-queue", "all");
export const SHIP_QUEUE_QUERY_KEY = ["floor", "ship", "queue"] as const;

/**
 * §4.8's order: promise date first (a NULL date sorts LAST — an order with no
 * promise is not more urgent than one with a date), then the order number.
 * Pure and exported so the sort is a tested table, not folklore.
 */
export function sortShipQueue(items: readonly OrderListItem[]): OrderListItem[] {
  return [...items].sort((a, b) => {
    if (a.promise_date !== b.promise_date) {
      if (a.promise_date === null) return 1;
      if (b.promise_date === null) return -1;
      return a.promise_date < b.promise_date ? -1 : 1;
    }
    if (a.order_no === b.order_no) return 0;
    return a.order_no < b.order_no ? -1 : 1;
  });
}

export function FloorShipQueueScreen() {
  const { t } = useTranslation();
  // The queue is `GET /v1/orders`, which needs sales.read. A role the mirror
  // says lacks it is told so without the request (a floor worker's tablet used
  // to draw a 403 first, D-088), and the read waits for the role feed rather
  // than racing it. A feed that failed, or a custom role the mirror does not
  // know, says nothing, so the server decides.
  const sales = useSalesPermissions();
  const knownRoles = rolesAreKnown(sales, SALES_ROLE_GRANTS);
  const notYours = sales.isResolved && !sales.isFeedError && knownRoles && !sales.has("sales.read");
  const roleChecking = !sales.isResolved;
  const read = useCachedRead<OrderListResponse>({
    queryKey: SHIP_QUEUE_QUERY_KEY,
    cacheKey: SHIP_QUEUE_CACHE_KEY,
    enabled: !notYours && !roleChecking,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/orders", {
          params: { query: { shippable: "true", limit: 50 } },
          signal: floorReadSignal(signal),
        }),
        "ship-queue",
      ),
  });

  const orders = sortShipQueue(read.data?.items ?? []);

  return (
    <FloorScreen
      wide
      title={t("sales.floor.ship.queueTitle")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      {notYours ? <ShipNotYours /> : null}
      {!notYours ? (
        <SnapshotNote
          source={read.source}
          cachedAt={read.cachedAt}
          revalidating={read.revalidating}
        />
      ) : null}
      {read.isLoading || (roleChecking && read.online && read.source === "none") ? (
        <FloorSkeleton rows={3} />
      ) : null}

      {/* A read that did not come back is NOT an empty queue. `source === "none"`
          is "no live answer and nothing cached": online that is a failed
          request (error + retry + support code), offline it is a device that
          has never loaded the queue. Rendering FloorEmpty off a 500 would tell
          a storekeeper "nothing to ship" while a truck waits — the counts
          list's rule (floor-counts.tsx), applied here. */}
      {!notYours && !(roleChecking && read.online) && read.source === "none" && !read.isLoading ? (
        read.online ? (
          floorForbidden(read.error) ? (
            /* THE ROLE, not the network, and not a fault (D-444 ruling 5):
               the server's own refusal, for a role the mirror cannot see. */
            <ShipNotYours />
          ) : (
            <FloorError
              title={t("sales.floor.ship.queueLoadFailedTitle")}
              detail={t("sales.floor.ship.queueLoadFailedBody")}
              requestId={floorRequestId(read.error)}
              onRetry={read.refetch}
            />
          )
        ) : (
          <FloorNeedsConnection what={t("sales.floor.ship.thisQueue")} />
        )
      ) : null}

      {!notYours && !read.isLoading && read.source !== "none" && orders.length === 0 ? (
        <FloorEmpty
          title={t("sales.floor.ship.queueEmpty")}
          body={t("sales.floor.ship.queueEmptyBody")}
        />
      ) : null}

      <ul className="floor-cards" data-testid="floor-ship-queue">
        {(notYours ? [] : orders).map((order) => {
          const allReserved = order.lines_total > 0 && order.lines_reserved === order.lines_total;
          return (
            <li key={order.id}>
              {/* The card is the link (the hub tile's own shape): one glove
                  target, one tap, the §3.7 budget's click 1. */}
              <Link
                to="/floor/ship/$orderId"
                params={{ orderId: order.id }}
                className="floor-card floor-ship-order"
                data-testid="floor-ship-order"
                data-order-id={order.id}
                data-all-reserved={allReserved ? "true" : "false"}
              >
                <span className="floor-card-title">{order.order_no}</span>
                <span className="floor-card-sub">{order.customer_display_name}</span>
                {/* §4.8's badge: "lines count, all-reserved indicator" — the two
                    integers the list item carries for exactly this, rendered as
                    reserved/total and toned OK only when every line has its
                    hold (a production line never does). */}
                <span
                  className={`floor-badge floor-ship-badge${allReserved ? " floor-ship-badge-ok" : ""}`}
                  data-testid="floor-ship-order-badge"
                >
                  {t("sales.floor.ship.badge", {
                    reserved: order.lines_reserved,
                    total: order.lines_total,
                  })}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </FloorScreen>
  );
}

/** `/floor/ship` is reachable by URL on a tablet whose hub shows no ship tile
 * (D-118 makes the tile ABSENT), and a red error with a request id read to a
 * floor worker as something broken. It is a calm sentence saying whose work
 * this is, with no retry, because no retry can change a role. */
function ShipNotYours() {
  const { t } = useTranslation();
  return (
    <div className="floor-state floor-state-empty" role="status" data-testid="floor-not-yours">
      <p className="floor-state-title">{t("home.floor.shipNotYoursTitle")}</p>
      <p className="floor-state-body">{t("home.floor.shipNotYoursBody")}</p>
    </div>
  );
}
